import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../lib/i18n';
import { recurringAPI, categoriesAPI, accountsAPI } from '../lib/api';
import { formatCurrency, formatDate, cn } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Switch } from '../components/ui/switch';
import {
  Plus, Trash2, Edit2, RefreshCw, Calendar as CalendarIcon,
  Play, ArrowUpRight, ArrowDownRight, Repeat
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const RecurringTransactions = () => {
  const { user, language } = useAuth();
  const t = useTranslation(language);

  const [recurring, setRecurring] = useState([]);
  const [categories, setCategories] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const [form, setForm] = useState({
    type: 'expense',
    amount: '',
    category_id: '',
    account_id: '',
    description: '',
    frequency: 'monthly',
    start_date: new Date(),
    is_active: true
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [recurringRes, categoriesRes, accountsRes] = await Promise.all([
        recurringAPI.getAll(),
        categoriesAPI.getAll(),
        accountsAPI.getAll()
      ]);
      setRecurring(recurringRes.data);
      setCategories(categoriesRes.data);
      setAccounts(accountsRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.amount || !form.category_id || !form.account_id) {
      toast.error('Completa los campos obligatorios');
      return;
    }

    try {
      const payload = {
        type: form.type,
        amount: parseFloat(form.amount),
        category_id: form.category_id,
        account_id: form.account_id,
        description: form.description,
        frequency: form.frequency,
        start_date: form.start_date.toISOString(),
        is_active: form.is_active
      };

      if (editingItem) {
        await recurringAPI.update(editingItem.id, payload);
        toast.success('Transacción recurrente actualizada');
      } else {
        await recurringAPI.create(payload);
        toast.success('Transacción recurrente creada');
      }

      setDialogOpen(false);
      setEditingItem(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta transacción recurrente?')) return;

    try {
      await recurringAPI.delete(id);
      toast.success('Transacción recurrente eliminada');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleExecute = async (id) => {
    try {
      await recurringAPI.execute(id);
      toast.success('Transacción ejecutada correctamente');
      fetchData();
    } catch (error) {
      toast.error('Error al ejecutar');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setForm({
      type: item.type,
      amount: item.amount.toString(),
      category_id: item.category_id,
      account_id: item.account_id,
      description: item.description,
      frequency: item.frequency,
      start_date: new Date(item.start_date),
      is_active: item.is_active
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setForm({
      type: 'expense',
      amount: '',
      category_id: '',
      account_id: '',
      description: '',
      frequency: 'monthly',
      start_date: new Date(),
      is_active: true
    });
  };

  const openNewDialog = () => {
    setEditingItem(null);
    resetForm();
    setDialogOpen(true);
  };

  const getCategoryName = (id) => categories.find(c => c.id === id)?.name || '-';
  const getAccountName = (id) => accounts.find(a => a.id === id)?.name || '-';
  const getCategoryColor = (id) => categories.find(c => c.id === id)?.color || '#64748B';

  const frequencyLabels = {
    daily: 'Diario',
    weekly: 'Semanal',
    monthly: 'Mensual',
    yearly: 'Anual'
  };

  const activeRecurring = recurring.filter(r => r.is_active);
  const monthlyTotal = activeRecurring
    .filter(r => r.frequency === 'monthly')
    .reduce((sum, r) => sum + (r.type === 'expense' ? -r.amount : r.amount), 0);

  return (
    <div className="space-y-6" data-testid="recurring-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display font-bold text-2xl lg:text-3xl text-slate-900 tracking-tight">
            Transacciones Recurrentes
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Automatiza tus ingresos y gastos periódicos
          </p>
        </div>
        <Button
          onClick={openNewDialog}
          className="bg-emerald-900 hover:bg-emerald-800 rounded-sm"
          data-testid="new-recurring-btn"
        >
          <Plus size={16} className="mr-2" />
          Nueva Recurrente
        </Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-slate-200 rounded-sm shadow-none">
          <CardContent className="p-6">
            <p className="text-xs uppercase tracking-widest font-semibold text-slate-400 mb-2">
              Activas
            </p>
            <p className="font-mono text-2xl font-medium text-slate-900">
              {activeRecurring.length}
            </p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 rounded-sm shadow-none">
          <CardContent className="p-6">
            <p className="text-xs uppercase tracking-widest font-semibold text-slate-400 mb-2">
              Impacto Mensual
            </p>
            <p className={cn(
              "font-mono text-2xl font-medium",
              monthlyTotal >= 0 ? "text-emerald-600" : "text-red-500"
            )}>
              {formatCurrency(monthlyTotal, user?.currency)}
            </p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 rounded-sm shadow-none">
          <CardContent className="p-6">
            <p className="text-xs uppercase tracking-widest font-semibold text-slate-400 mb-2">
              Próxima Ejecución
            </p>
            <p className="text-sm text-slate-900">
              {activeRecurring.length > 0 && activeRecurring[0].next_execution
                ? formatDate(activeRecurring[0].next_execution)
                : '-'
              }
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Recurring List */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <RefreshCw className="w-6 h-6 animate-spin text-emerald-900" />
        </div>
      ) : recurring.length === 0 ? (
        <Card className="border-slate-200 rounded-sm shadow-none">
          <CardContent className="flex flex-col items-center justify-center h-48 text-slate-400">
            <Repeat size={32} className="mb-2" />
            <p>No hay transacciones recurrentes</p>
            <Button variant="link" onClick={openNewDialog} className="text-emerald-900">
              Crear primera recurrente
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-slate-200 rounded-sm shadow-none">
          <CardContent className="p-0">
            <div className="divide-y divide-slate-100">
              {recurring.map(item => (
                <div key={item.id}
                  className={cn("flex items-start gap-3 p-4 hover:bg-slate-50 transition-colors",
                    !item.is_active && "opacity-50"
                  )}
                  data-testid={`recurring-${item.id}`}
                >
                  <div className={cn("w-9 h-9 rounded-sm flex items-center justify-center flex-shrink-0 mt-0.5",
                    item.type === 'income' ? 'bg-emerald-50' : 'bg-red-50'
                  )}>
                    {item.type === 'income'
                      ? <ArrowUpRight className="w-4 h-4 text-emerald-600" strokeWidth={1.5} />
                      : <ArrowDownRight className="w-4 h-4 text-red-500" strokeWidth={1.5} />
                    }
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-1.5 mb-0.5">
                      <p className="font-medium text-slate-900 text-sm truncate max-w-[120px] sm:max-w-none">
                        {item.description || getCategoryName(item.category_id)}
                      </p>
                      <span className="text-xs px-1.5 py-0.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: `${getCategoryColor(item.category_id)}15`, color: getCategoryColor(item.category_id) }}
                      >
                        {getCategoryName(item.category_id)}
                      </span>
                      <span className="text-xs px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 flex-shrink-0">
                        {frequencyLabels[item.frequency]}
                      </span>
                    </div>
                    <p className="text-xs text-slate-400">
                      {getAccountName(item.account_id)} · Próximo: {item.next_execution ? formatDate(item.next_execution) : '-'}
                    </p>
                    <p className={cn("font-mono font-semibold text-sm mt-1",
                      item.type === 'income' ? 'text-emerald-600' : 'text-red-500'
                    )}>
                      {item.type === 'income' ? '+' : '-'}{formatCurrency(item.amount, user?.currency)}
                    </p>
                  </div>

                  <div className="flex items-center gap-0.5 flex-shrink-0">
                    <Button variant="ghost" size="sm" onClick={() => handleExecute(item.id)} title="Ejecutar ahora" className="h-8 w-8 p-0">
                      <Play size={15} />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(item)} className="h-8 w-8 p-0">
                      <Edit2 size={15} />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)} className="h-8 w-8 p-0">
                      <Trash2 size={15} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="recurring-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editingItem ? 'Editar Recurrente' : 'Nueva Recurrente'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Type selector */}
            <div className="grid grid-cols-2 gap-2">
              <Button
                type="button"
                variant={form.type === 'expense' ? 'default' : 'outline'}
                className={cn("rounded-sm", form.type === 'expense' && 'bg-red-500 hover:bg-red-600')}
                onClick={() => setForm(prev => ({ ...prev, type: 'expense' }))}
              >
                <ArrowDownRight size={16} className="mr-2" />
                Gasto
              </Button>
              <Button
                type="button"
                variant={form.type === 'income' ? 'default' : 'outline'}
                className={cn("rounded-sm", form.type === 'income' && 'bg-emerald-600 hover:bg-emerald-700')}
                onClick={() => setForm(prev => ({ ...prev, type: 'income' }))}
              >
                <ArrowUpRight size={16} className="mr-2" />
                Ingreso
              </Button>
            </div>

            {/* Amount */}
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">
                Monto *
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">€</span>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={form.amount}
                  onChange={(e) => setForm(prev => ({ ...prev, amount: e.target.value }))}
                  className="pl-8 font-mono rounded-sm"
                  required
                />
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">
                Descripción
              </Label>
              <Input
                value={form.description}
                onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                className="rounded-sm"
                placeholder="Ej: Netflix, Alquiler, Nómina..."
              />
            </div>

            {/* Category and Account */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">
                  Categoría *
                </Label>
                <Select value={form.category_id} onValueChange={(v) => setForm(prev => ({ ...prev, category_id: v }))}>
                  <SelectTrigger className="rounded-sm">
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">
                  Cuenta *
                </Label>
                <Select value={form.account_id} onValueChange={(v) => setForm(prev => ({ ...prev, account_id: v }))}>
                  <SelectTrigger className="rounded-sm">
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    {accounts.map(acc => (
                      <SelectItem key={acc.id} value={acc.id}>{acc.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Frequency and Start Date */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">
                  Frecuencia
                </Label>
                <Select value={form.frequency} onValueChange={(v) => setForm(prev => ({ ...prev, frequency: v }))}>
                  <SelectTrigger className="rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Diario</SelectItem>
                    <SelectItem value="weekly">Semanal</SelectItem>
                    <SelectItem value="monthly">Mensual</SelectItem>
                    <SelectItem value="yearly">Anual</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">
                  Fecha Inicio
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left rounded-sm">
                      <CalendarIcon size={16} className="mr-2 text-slate-400" />
                      {format(form.start_date, 'dd/MM/yyyy')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={form.start_date}
                      onSelect={(date) => date && setForm(prev => ({ ...prev, start_date: date }))}
                      locale={es}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Active toggle */}
            <div className="flex items-center justify-between">
              <Label className="text-sm text-slate-600">Activa</Label>
              <Switch
                checked={form.is_active}
                onCheckedChange={(checked) => setForm(prev => ({ ...prev, is_active: checked }))}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">
                Cancelar
              </Button>
              <Button type="submit" className="bg-emerald-900 hover:bg-emerald-800 rounded-sm">
                Guardar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RecurringTransactions;
